import React, { useState } from "react";
import { useAuthActions } from "@convex-dev/auth/react";
import {
  Authenticated,
  Unauthenticated,
  AuthLoading,
  useQuery,
  useMutation,
} from "convex/react";
import { api } from "@/convex/_generated/api";
import { LogIn, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * بوابة الدخول.
 *
 * التحقق يجري في الخادم عبر Convex Auth، ولا تُرسل كلمة المرور إلى
 * المتصفح في أي لحظة. البوابة السابقة كانت تجلب الكلمة وتقارنها هنا،
 * فكان يكفي فتح الصفحة لقراءتها.
 */
const SignInForm = () => {
  const { signIn } = useAuthActions();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await signIn("password", { email, password, flow: "signIn" });
    } catch {
      setError("بيانات الدخول غير صحيحة");
      setPassword("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl border bg-card p-6 shadow-sm"
      >
        <div className="mb-5 text-center">
          <img src="/logo.png" alt="شعار نظام قطر للتعليم" className="mx-auto mb-4 h-24 w-24 object-contain" />
          <h1 className="text-lg font-bold text-primary">
            نظام متابعة التعليم الإلكتروني
          </h1>
          <p className="mt-1 text-xs text-muted-foreground">
            مدرسة ابن تيمية الثانوية للبنين
          </p>
        </div>

        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">
            الحساب
          </span>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            dir="ltr"
            required
          />
        </label>

        <label className="mb-4 block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">
            كلمة المرور
          </span>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            dir="ltr"
            required
          />
        </label>

        {error && (
          <p className="mb-3 flex items-center gap-1.5 text-xs text-destructive">
            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
            {error}
          </p>
        )}

        <Button type="submit" disabled={busy} className="w-full">
          {busy ? (
            <>
              <Loader2 className="ml-1.5 h-4 w-4 animate-spin" /> جارٍ الدخول…
            </>
          ) : (
            <>
              <LogIn className="ml-1.5 h-4 w-4" /> دخول
            </>
          )}
        </Button>

        <p className="mt-4 text-center text-[11px] text-muted-foreground/70">
          الحسابات يُنشئها منسق المشاريع الإلكترونية
        </p>
      </form>
    </div>
  );
};

/**
 * شاشة إنشاء حساب المنسق. تظهر مرة واحدة فقط: متى وُجد أي مستخدم
 * أغلقت نفسها، فلا سبيل لأحد لإنشاء حساب لنفسه بعدها.
 */
const BootstrapForm = () => {
  const bootstrap = useMutation(api.users.bootstrapFirstAdmin);
  const { signIn } = useAuthActions();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return setError("كلمتا المرور غير متطابقتين");
    if (password.length < 8) return setError("كلمة المرور أقصر من ثمانية أحرف");
    setBusy(true);
    setError("");
    try {
      await bootstrap({ email, password });
      await signIn("password", { email, password, flow: "signIn" });
    } catch (err) {
      setError(String(err).includes("من قبل") ? "الحساب أُنشئ بالفعل" : "تعذّر إنشاء الحساب");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border bg-card p-6 shadow-sm">
        <div className="mb-5 text-center">
          <img src="/logo.png" alt="شعار نظام قطر للتعليم" className="mx-auto mb-4 h-24 w-24 object-contain" />
          <h1 className="text-lg font-bold text-primary">إنشاء حساب المنسق</h1>
          <p className="mt-1 text-xs text-muted-foreground">
            أول حساب في النظام، وله كل الصلاحيات
          </p>
        </div>

        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">البريد</span>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} dir="ltr" required />
        </label>
        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">كلمة المرور</span>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} dir="ltr" required />
        </label>
        <label className="mb-4 block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">تأكيد كلمة المرور</span>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} dir="ltr" required />
        </label>

        {error && (
          <p className="mb-3 flex items-center gap-1.5 text-xs text-destructive">
            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
            {error}
          </p>
        )}

        <Button type="submit" disabled={busy} className="w-full">
          {busy ? (<><Loader2 className="ml-1.5 h-4 w-4 animate-spin" /> جارٍ الإنشاء…</>) : "إنشاء الحساب والدخول"}
        </Button>
      </form>
    </div>
  );
};

const Entry = () => {
  const needsBootstrap = useQuery(api.users.needsBootstrap);
  if (needsBootstrap === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  return needsBootstrap ? <BootstrapForm /> : <SignInForm />;
};

const SignInGate = ({ children }) => (
  <>
    <AuthLoading>
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    </AuthLoading>
    <Unauthenticated>
      <Entry />
    </Unauthenticated>
    <Authenticated>{children}</Authenticated>
  </>
);

export default SignInGate;

